import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CustomHttpParamEncoder } from '../helpers';
import { ENV } from '@tnq/shared/config';
import { IEnv, IParamValue, IQueryParams } from '@tnq/shared/types';

@Injectable({ providedIn: 'root' })
export class ApiService {
  httpClient = inject(HttpClient);

  protected apiUrl!: string;

  constructor() {
    const env = inject<IEnv>(ENV);

    this.apiUrl = env.apiUrl;
  }

  public get$<T>(endpoint: string, params?: IQueryParams): Observable<T> {
    return this.httpClient.get<T>(`${this.apiUrl}${endpoint}`, {
      params: new HttpParams({
        fromObject: removeEmptyFields(params),
        encoder: new CustomHttpParamEncoder(),
      }),
    });
  }

  public post$<TResponse, TBody>(
    endpoint: string,
    data: TBody,
    headers?: Record<string, string>,
  ): Observable<TResponse> {
    return this.httpClient.post<TResponse>(`${this.apiUrl}${endpoint}`, data, {
      headers,
    });
  }

  public put$<TResponse, TBody>(
    endpoint: string,
    body: TBody,
    params?: IQueryParams,
  ): Observable<TResponse> {
    return this.httpClient.put<TResponse>(`${this.apiUrl}${endpoint}`, body, {
      params: new HttpParams({
        fromObject: removeEmptyFields(params),
        encoder: new CustomHttpParamEncoder(),
      }),
    });
  }

  public putFile$<TResponse>(
    endpoint: string,
    body: File,
    binaryFile: ArrayBuffer,
    params?: IQueryParams,
  ): Observable<TResponse> {
    return this.httpClient.put<TResponse>(`${endpoint}`, binaryFile, {
      params: new HttpParams({
        fromObject: removeEmptyFields(params),
        encoder: new CustomHttpParamEncoder(),
      }),
      headers: {
        'Content-Type': body.type,
      },
    });
  }

  public patch$<TResponse, TBody>(
    endpoint: string,
    body: TBody,
    params?: IQueryParams,
  ): Observable<TResponse> {
    return this.httpClient.patch<TResponse>(`${this.apiUrl}${endpoint}`, body, {
      params: new HttpParams({
        fromObject: removeEmptyFields(params),
        encoder: new CustomHttpParamEncoder(),
      }),
    });
  }

  public delete$<TResponse>(endpoint: string): Observable<TResponse> {
    return this.httpClient.delete<TResponse>(`${this.apiUrl}${endpoint}`, {});
  }
}

export function removeEmptyFields(
  obj: object | undefined,
): { [key: string]: IParamValue } | undefined {
  return obj
    ? Object.fromEntries(Object.entries(obj).filter(([, value]) => !!value))
    : obj;
}
